import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';

export function SubscriptionsTab({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [featureInput, setFeatureInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ id: '', name: '', price: 0, billing: 'monthly', features: [] });
  const [createFeatureInput, setCreateFeatureInput] = useState('');

  const fetchPlans = () => {
    axios.get(`${API}/admin/subscriptions/plans`, { withCredentials: true }).then(r => setPlans(r.data)).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/admin/subscriptions`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ subscriptions: [], total: 0, active: 0, plans: {} }));
    fetchPlans();
  }, []);

  const formatPrice = (p) => {
    if (p.price === 0) return 'Gratuit';
    if (p.billing === 'annual') return `${p.price}\u20AC/an`;
    return `${p.price}\u20AC/mois`;
  };

  const startEdit = (p) => {
    setEditingPlan(p.id);
    setEditForm({ name: p.name, price: p.price, billing: p.billing || 'monthly', features: [...(p.features || [])],
      has_ads: !!p.has_ads, max_screens: p.max_screens || 1, max_profiles: p.max_profiles || 2 });
    setFeatureInput('');
  };

  const cancelEdit = () => { setEditingPlan(null); setEditForm(null); };

  const deletePlan = async (planId) => {
    if (!window.confirm('Supprimer ce plan ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/admin/subscriptions/plans/${planId}`, { withCredentials: true });
      fetchPlans();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const createPlan = async () => {
    if (!createForm.id || !createForm.name) { alert('ID et nom requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/subscriptions/plans`, { ...createForm, price: parseFloat(createForm.price) || 0 }, { withCredentials: true });
      setShowCreate(false); setCreateForm({ id: '', name: '', price: 0, billing: 'monthly', features: [] });
      fetchPlans();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/subscriptions/plans/${editingPlan}`, editForm, { withCredentials: true });
      setEditingPlan(null); setEditForm(null);
      fetchPlans();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const removeFeat = (idx) => { setEditForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) })); };
  const addFeat = () => { if (featureInput.trim()) { setEditForm(prev => ({ ...prev, features: [...prev.features, featureInput.trim()] })); setFeatureInput(''); } };
  const se = (k, v) => setEditForm(prev => ({ ...prev, [k]: v }));

  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400' : 'bg-white/5 border border-white/8 text-white';
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';

  return (
    <div>
      <h1 className={`text-2xl font-bold mb-6 ${light ? 'text-gray-900' : 'text-white'}`}>Abonnements</h1>
      <div className={`rounded-xl p-5 border mb-6 ${cardCls}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-sm font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>Plans disponibles</h3>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="add-plan-btn" className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"><Plus size={12} /> Ajouter un plan</button>
        </div>
        {showCreate && (
          <div className={`rounded-lg border p-4 mb-4 ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/5 border-white/8'}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              <input value={createForm.id} onChange={e => setCreateForm(p => ({ ...p, id: e.target.value.toLowerCase().replace(/\s/g, '_') }))} placeholder="ID (ex: student)" className={`rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
              <input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="Nom du plan" className={`rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
              <input type="number" step="0.01" value={createForm.price} onChange={e => setCreateForm(p => ({ ...p, price: e.target.value }))} placeholder="Prix" className={`rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
              <select value={createForm.billing} onChange={e => setCreateForm(p => ({ ...p, billing: e.target.value }))} className={`rounded-lg px-2 py-1.5 text-xs ${inputCls}`}>
                <option value="monthly">Mensuel</option><option value="annual">Annuel</option>
              </select>
            </div>
            <div className="mb-2">
              <div className="flex flex-wrap gap-1 mb-1">
                {createForm.features.map((f, i) => <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-[#E9641A]/20 text-[#E9641A] rounded text-[10px]">{f}<button onClick={() => setCreateForm(p => ({ ...p, features: p.features.filter((_, j) => j !== i) }))}><X size={8} /></button></span>)}
              </div>
              <div className="flex gap-1">
                <input value={createFeatureInput} onChange={e => setCreateFeatureInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (createFeatureInput.trim()) { setCreateForm(p => ({ ...p, features: [...p.features, createFeatureInput.trim()] })); setCreateFeatureInput(''); } } }} placeholder="+ avantage" className={`flex-1 rounded px-2 py-1 text-[10px] focus:outline-none ${inputCls}`} />
                <button onClick={() => { if (createFeatureInput.trim()) { setCreateForm(p => ({ ...p, features: [...p.features, createFeatureInput.trim()] })); setCreateFeatureInput(''); } }} className="text-[#E9641A] text-xs font-bold px-2">+</button>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setShowCreate(false)} className={`px-3 py-1 text-xs border rounded ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/40'}`}>Annuler</button>
              <button onClick={createPlan} disabled={saving} className="px-3 py-1 text-xs bg-[#E9641A] text-white rounded font-semibold">{saving ? '...' : 'Créer le plan'}</button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {plans.map(p => (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`border rounded-lg p-4 transition-colors relative ${
              p.id === 'premium_annual'
                ? 'border-[#E9641A] ring-1 ring-[#E9641A]/30'
                : light ? 'border-gray-200 hover:border-gray-300' : 'border-white/8 hover:border-[#E9641A]/30'
            }`}>
              {p.billing === 'annual' && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#E9641A] text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">2 MOIS OFFERTS</span>
              )}
              {editingPlan === p.id && editForm ? (
                <div className="space-y-2">
                  <input value={editForm.name} onChange={e => se('name', e.target.value)} className={`w-full rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} placeholder="Nom" />
                  <div className="flex gap-1.5">
                    <input type="number" step="0.01" value={editForm.price} onChange={e => se('price', parseFloat(e.target.value) || 0)} className={`w-full rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} placeholder="Prix" />
                    <select value={editForm.billing} onChange={e => se('billing', e.target.value)} className={`rounded-lg px-2 py-1.5 text-xs ${inputCls}`}>
                      <option value="monthly">/ mois</option>
                      <option value="annual">/ an</option>
                    </select>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="flex-1">
                      <p className={`text-[9px] mb-0.5 ${light ? 'text-gray-500' : 'text-white/40'}`}>Écrans max</p>
                      <input type="number" min="1" value={editForm.max_screens} onChange={e => se('max_screens', parseInt(e.target.value) || 1)} className={`w-full rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} data-testid={`plan-max-screens-${p.id}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-[9px] mb-0.5 ${light ? 'text-gray-500' : 'text-white/40'}`}>Profils max</p>
                      <input type="number" min="1" value={editForm.max_profiles} onChange={e => se('max_profiles', parseInt(e.target.value) || 1)} className={`w-full rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} data-testid={`plan-max-profiles-${p.id}`} />
                    </div>
                  </div>
                  <label className={`flex items-center gap-2 text-[10px] cursor-pointer ${light ? 'text-gray-600' : 'text-white/60'}`}>
                    <input type="checkbox" checked={!!editForm.has_ads} onChange={e => se('has_ads', e.target.checked)} className="accent-[#E9641A]" data-testid={`plan-has-ads-${p.id}`} />
                    Offre avec publicités (pré-roll / mid-roll)
                  </label>
                  <div>
                    <p className={`text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/40'}`}>Avantages :</p>
                    {editForm.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-1 mb-0.5">
                        <span className={`text-[10px] flex-1 ${light ? 'text-gray-600' : 'text-white/60'}`}>{f}</span>
                        <button onClick={() => removeFeat(i)} className="text-red-400 hover:text-red-300"><X size={10} /></button>
                      </div>
                    ))}
                    <div className="flex gap-1 mt-1">
                      <input value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeat())} placeholder="+ avantage" className={`flex-1 rounded px-2 py-0.5 text-[10px] focus:outline-none ${inputCls}`} />
                      <button onClick={addFeat} className="text-[#E9641A] text-[10px] font-bold">+</button>
                    </div>
                  </div>
                  <div className="flex gap-1 pt-1">
                    <button onClick={cancelEdit} className={`flex-1 px-2 py-1 text-[10px] border rounded ${light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/40'}`}>Annuler</button>
                    <button onClick={saveEdit} disabled={saving} className="flex-1 px-2 py-1 text-[10px] bg-[#E9641A] text-white rounded font-semibold">{saving ? '...' : 'Sauvegarder'}</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <p className={`text-base font-bold mb-1 ${light ? 'text-gray-800' : 'text-white'}`}>{p.name}</p>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => startEdit(p)} data-testid={`edit-plan-${p.id}`} className={`p-1 rounded transition-colors ${light ? 'text-gray-300 hover:text-gray-600 hover:bg-gray-100' : 'text-white/20 hover:text-white hover:bg-white/10'}`}><Edit size={12} /></button>
                      <button onClick={() => deletePlan(p.id)} data-testid={`delete-plan-${p.id}`} className={`p-1 rounded transition-colors ${light ? 'text-gray-300 hover:text-red-500 hover:bg-red-50' : 'text-white/20 hover:text-red-400 hover:bg-red-400/10'}`}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#E9641A] mb-3">{formatPrice(p)}</p>
                  <p className={`text-[10px] mb-2 ${light ? 'text-gray-400' : 'text-white/35'}`}>
                    {p.max_screens || 1} écran{(p.max_screens || 1) > 1 ? 's' : ''} · {p.max_profiles || 2} profils · {p.has_ads ? 'avec pub' : 'sans pub'}
                  </p>
                  <ul className="space-y-1">{p.features?.map((f, i) => <li key={i} className={`text-xs flex items-center gap-1.5 ${light ? 'text-gray-500' : 'text-white/50'}`}><Check size={12} className="text-green-400 flex-none" />{f}</li>)}</ul>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      <p className={`text-xs ${light ? 'text-gray-400' : 'text-white/30'}`}>Les modifications sont synchronisées avec Stripe et visibles côté utilisateur en temps réel.</p>
    </div>
  );
}

